/**
 * The shop's actions (Acties) on the till. The server applies them when it prices an order; the
 * till only needs them to show the day price and the "Dagpromo" label on the tiles, and the text
 * of the automatic discount on the ticket.
 */
import type { Category, Channel, DayPromotion, Menu, Product, Promotions } from "../../api/types";

type Action = Promotions[keyof Promotions];

/** On, and chosen for this channel. */
export function appliesTo(action: Pick<Action, "on" | "channels">, channel: Channel): boolean {
  return action.on && action.channels[channel];
}

/** The weekday in Belgium (0 = Sunday), whatever the device's own time zone. */
export function belgianWeekday(at: Date): number {
  const name = new Intl.DateTimeFormat("en-US", { timeZone: "Europe/Brussels", weekday: "short" }).format(at);
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(name);
}

/** The Dagpromo that holds for the product on this channel today (the first one when both do), or null. */
export function activeDay(
  promotions: Promotions | null,
  channel: Channel,
  product: Product,
  at: Date = new Date(),
): DayPromotion | null {
  if (!promotions) return null;
  const weekday = belgianWeekday(at);
  return (
    [promotions.day, promotions.day2].find(
      (day) =>
        appliesTo(day, channel) &&
        day.weekdays.includes(weekday) &&
        product.categoryId === day.categoryId &&
        !day.excludeProductIds.includes(product.id) &&
        product.variants.some((variant) => day.prices[variant.label] !== undefined),
    ) ?? null
  );
}

/** The product takes part in a day promotion on this channel today. */
export function inDayPromotion(
  promotions: Promotions | null,
  channel: Channel,
  product: Product,
  at: Date = new Date(),
): boolean {
  return activeDay(promotions, channel, product, at) !== null;
}

/** The price in euros of a product in a size: the day price when it applies, else the menu price. */
export function priceOf(
  promotions: Promotions | null,
  channel: Channel,
  product: Product,
  variantLabel: string,
  at: Date = new Date(),
): number {
  const variant = product.variants.find((item) => item.label === variantLabel);
  const dayPrice = activeDay(promotions, channel, product, at)?.prices[variantLabel];
  return dayPrice ?? variant?.price ?? 0;
}

/** Drinks: their category is 21% on site, or they hold alcohol (same rule as the server's points). */
export function isDrink(menu: Menu, product: Product): boolean {
  if (product.alcohol === true) return true;
  const category: Category | undefined = menu.categories.find((item) => item.id === product.categoryId);
  return category?.vat?.onSite === 21;
}

export const WEEKDAYS = ["zondag", "maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag"] as const;

/** Monday first, as the week is shown (0 = Sunday). */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

/** The sizes of the products of a category, in the order of the menu ("Small", "Medium", "Large"). */
export function categorySizes(menu: Menu, categoryId: string | null): string[] {
  const sizes: string[] = [];
  for (const product of Object.values(menu.products)) {
    if (categoryId === null || product.categoryId !== categoryId) continue;
    for (const variant of product.variants) if (!sizes.includes(variant.label)) sizes.push(variant.label);
  }
  return sizes;
}

/**
 * Another category for the day promotion: the day prices of sizes it also has stay, the specialties
 * of the category (Stel zelf je pizza samen, Splitza) do not take part.
 */
export function dayForCategory(day: Promotions["day"], menu: Menu, categoryId: string): Promotions["day"] {
  const sizes = categorySizes(menu, categoryId);
  return {
    ...day,
    categoryId,
    prices: Object.fromEntries(Object.entries(day.prices).filter(([size]) => sizes.includes(size))),
    excludeProductIds: Object.values(menu.products)
      .filter((product) => product.categoryId === categoryId && product.special === true)
      .map((product) => product.id),
  };
}

/** "Dagpromo pizza" (prototype) for the pizzas, otherwise "Dagpromo" with the name of the category. */
export function dayPromotionName(menu: Menu, day: DayPromotion, fallback = "Dagpromo"): string {
  const category = menu.categories.find((item) => item.id === day.categoryId);
  if (!category) return fallback;
  return /^pizza/i.test(category.name) ? "Dagpromo pizza" : `Dagpromo ${category.name.toLowerCase()}`;
}

/**
 * "Maandag: small 8,50 · medium 11,50 · large 14,50", as the prototype describes the day promotion.
 * The sizes follow the menu (the stored settings do not keep their order).
 */
export function dayDescription(day: DayPromotion, menu: Menu): string {
  const days = WEEK_ORDER.filter((weekday) => day.weekdays.includes(weekday)).map((d) => WEEKDAYS[d]);
  const sizes = categorySizes(menu, day.categoryId);
  const position = (size: string) => (sizes.includes(size) ? sizes.indexOf(size) : sizes.length);
  const prices = Object.entries(day.prices)
    .sort(([a], [b]) => position(a) - position(b))
    .map(([size, price]) => `${size.toLowerCase()} ${price.toFixed(2).replace(".", ",")}`)
    .join(" · ");
  const when = days.length === 7 ? "Elke dag" : days.length === 0 ? "Geen dag gekozen" : days.join(", ");
  return `${when.charAt(0).toUpperCase()}${when.slice(1)}: ${prices}`;
}
